import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useSEO from '../../../hooks/useSEO';
import AnswerCapsule from '../../../components/AnswerCapsule';
import { cloudinaryUrl } from '../../../utils/cloudinaryUrl';
import CardImage from '../../../components/CardImage';
import './VehicleHub.css';
import { vehiclePath } from '../../../utils/vehicleSlug';
import { blogIndex } from '../../../data/blogIndex';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const BASE_URL = 'https://www.buyselltaxi.com';

// How many listing cards a hub shows, and therefore how many its ItemList names.
const HUB_CARDS = 6;

const VehicleHub = ({ config }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const pageUrl = `${BASE_URL}${config.slug}`;
  const guidePosts = (config.guides || [])
    .map(slug => blogIndex.find(p => p.slug === slug))
    .filter(Boolean);

  useEffect(() => {
    axios
      // brand + model. The API ignored `model` until 2026-09-30, so this returned
      // the whole brand and the hub listed other models under this one's heading.
      .get(`${API_URL}/buyer/vehicles?brand=${encodeURIComponent(config.brand)}&model=${encodeURIComponent(config.model)}&sort=newest`)
      .then(res => setListings(res.data.listings || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [config.brand, config.model]);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Browse Vehicles', item: `${BASE_URL}/vehicles` },
        { '@type': 'ListItem', position: 3, name: config.breadcrumbLabel, item: pageUrl },
      ],
    },
    // No AutoDealer here. public/index.html ships the business entity sitewide
    // under @id https://www.buyselltaxi.com/#organization; these four hubs used
    // to add a second AutoDealer with no @id — two businesses on one page, the
    // duplicate-entity defect removed everywhere else on 2026-09-19.
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: config.faqs.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
    ...(listings.length > 0
      ? [{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: config.breadcrumbLabel,
          url: pageUrl,
          // Exactly the cards the page shows (HUB_CARDS) — the list used to say
          // 12 items, contain 10 and display 6.
          numberOfItems: Math.min(listings.length, HUB_CARDS),
          itemListElement: listings.slice(0, HUB_CARDS).map((v, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `${BASE_URL}${vehiclePath(v)}`,
            name: v.title,
          })),
        }]
      : []),
  ];

  useSEO({
    title: config.seoTitle,
    description: config.seoDescription,
    keywords: config.keywords,
    url: pageUrl,
    image: `${BASE_URL}/og/landing${config.slug}.png`,
    jsonLd,
  });

  return (
    <div className="hub-page">
      {/* HERO */}
      <section className="hub-hero">
        <div className="hub-hero-inner">
          <nav className="hub-breadcrumb">
            <Link to="/">Home</Link> <span>/</span>
            <Link to="/vehicles">Browse Vehicles</Link> <span>/</span>
            <span>{config.breadcrumbLabel}</span>
          </nav>
          <h1>{config.h1}</h1>
          <AnswerCapsule
            question={config.seoTitle}
            answer={config.answerCapsule}
          />
          <Link
            to={`/vehicles?brand=${encodeURIComponent(config.brand)}&search=${encodeURIComponent(config.model)}`}
            className="hub-cta-btn"
          >
            View All {config.displayName} Listings →
          </Link>
        </div>
      </section>

      {/* SPECS TABLE */}
      <section className="hub-section">
        <h2>Key Specifications</h2>
        <div className="hub-table-wrap">
          <table className="hub-specs-table">
            <thead>
              <tr>
                <th>Spec</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {config.specs.map(([label, value]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* PRICE TABLE */}
      <section className="hub-section">
        <h2>Typical Resale Price Range — Delhi NCR ({new Date().getFullYear()})</h2>
        <div className="hub-table-wrap">
          <table className="hub-price-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Avg. Km Run</th>
                <th>Price Range</th>
                <th>Condition</th>
              </tr>
            </thead>
            <tbody>
              {config.priceData.map(row => (
                <tr key={row[0]}>
                  <td>{row[0]}</td>
                  <td>{row[1]}</td>
                  <td>{row[2]}</td>
                  <td>{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="hub-disclaimer">Prices are indicative market estimates for commercial/yellow-plate registered vehicles in Delhi NCR. Actual prices vary by condition, mileage, and registration type.</p>
      </section>

      {/* LIVE LISTINGS */}
      <section className="hub-section">
        <h2>Live Listings — {config.displayName} for Sale</h2>
        {loading ? (
          <p className="hub-loading">Loading listings…</p>
        ) : listings.length === 0 ? (
          <p className="hub-empty">No listings at the moment. <Link to="/vehicles">Browse all vehicles</Link>.</p>
        ) : (
          <div className="hub-listings-grid">
            {listings.slice(0, HUB_CARDS).map(v => (
              <Link key={v.id} to={vehiclePath(v)} className="hub-listing-card">
                <div className="hub-card-img">
                  <CardImage
                    src={cloudinaryUrl(v.images?.[0] || '', 400)}
                    alt={`${v.year} ${v.brand} ${v.model} used taxi for sale - ${v.city}`}
                  />
                  {v.badge_type === 'bst_listing' && <span className="hub-badge bst">BST Verified</span>}
                </div>
                <div className="hub-card-body">
                  <h3>{v.title}</h3>
                  <div className="hub-card-meta">
                    <span>{v.year}</span>
                    <span>{v.km_run?.toLocaleString()} km</span>
                    <span>{v.fuel_type}</span>
                    <span>{v.city}</span>
                  </div>
                  <p className="hub-card-price">₹{Number(v.price_final).toLocaleString('en-IN')}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
        {listings.length > 0 && (
          <div className="hub-view-all">
            <Link to={`/vehicles?brand=${encodeURIComponent(config.brand)}&search=${encodeURIComponent(config.model)}`} className="hub-cta-btn">
              View All {listings.length}+ Listings →
            </Link>
          </div>
        )}
      </section>

      {/* WHY COMMERCIAL */}
      <section className="hub-section hub-why">
        <h2>Why {config.displayName} is Popular for Commercial Use</h2>
        <ul className="hub-why-list">
          {config.whyPoints.map((pt, i) => (
            <li key={i}>{pt}</li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="hub-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {config.faqs.map(({ q, a }, i) => (
            <details key={i} className="faq-item">
              <summary className="faq-q">{q}</summary>
              <p className="faq-a">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* MODEL GUIDES — the hub's spokes. Until 2026-09-28 the four hubs linked
          to no blog post at all, while ~20 guides about these same models had
          one or two links from anywhere. Titles come from the generated
          blogIndex so they cannot drift from the posts. */}
      {guidePosts.length > 0 && (
        <section className="hub-section hub-internal-links">
          <h2>{config.displayName} Guides</h2>
          <ul>
            {guidePosts.map(p => (
              <li key={p.slug}><Link to={`/blog/${p.slug}`}>{p.title}</Link></li>
            ))}
          </ul>
        </section>
      )}

      {/* INTERNAL LINKS — indexable pages with stock only. /buy-taxi-mumbai was
          here and is noindexed (no Mumbai stock); Gurugram holds 27 cars. */}
      <section className="hub-section hub-internal-links">
        <h2>Explore More on Buy Sell Taxi</h2>
        <ul>
          <li><Link to="/vehicles">Browse All Commercial Vehicles</Link></li>
          <li><Link to="/buy-taxi-delhi">Buy Taxi in Delhi</Link></li>
          <li><Link to="/buy-taxi-gurugram">Buy Taxi in Gurugram</Link></li>
          <li><Link to="/t-permit">T-Permit Guide</Link></li>
          <li><Link to="/blog">Taxi Buying Guides &amp; Tips</Link></li>
        </ul>
      </section>
    </div>
  );
};

export default VehicleHub;
